<template>
    <div class="file-upload">
        <div class="upload-row">
            <!-- دکمه انتخاب فایل -->
            <input type="file" id="fileInput" ref="fileInput" class="hidden-input" @change="onFileSelected" multiple />
            <div class="choose-col">
                <BaseButton
                    class="choose-file-btn"
                    :title="selectedFiles.length > 0 ? `${selectedFiles.length} فایل انتخاب شده` : 'انتخاب فایل'"
                    variant="success"
                    @click="openFilePicker"
                >
                    <template #icon>
                        <font-awesome-icon :icon="['fas', 'file']" class="btn-icon" />
                    </template>
                </BaseButton>
            </div>

            <!-- توضیح -->
            <div class="desc-col">
                <input v-model="uploadFileData.description" placeholder="توضیحات (اختیاری)" class="desc-input" />
            </div>
        </div>

        <!-- نمایش فایل‌های انتخاب شده -->
        <div class="file-list" v-if="selectedFiles.length > 0">
            <div v-for="(file, index) in selectedFiles" :key="index" class="file-item">
                <span>{{ file.name }} ({{ formatFileSize(file.size) }})</span>
                <button @click="removeFile(index)" class="remove-btn">×</button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref } from 'vue';
import BaseButton from '@/components/UiCompunents/BaseButton.vue'

// eslint-disable-next-line no-undef
const props = defineProps({ recordId: { type: Number, required: true } });
// eslint-disable-next-line no-undef
const emit = defineEmits(['uploaded', 'confirm']);
// eslint-disable-next-line no-undef
defineExpose({ confirm });

const selectedFiles = ref([]);
const fileInput = ref(null)
const uploadFileData = ref({
    description: '',
    selectedList: []
});

function openFilePicker() {
    fileInput.value?.click()
}

// تنظیمات فشرده‌سازی تصاویر
const COMPRESSION_SETTINGS = {
    maxWidth: 1200, // حداکثر عرض تصویر
    maxHeight: 1200, // حداکثر ارتفاع تصویر
    quality: 0.7, // کیفیت تصویر (0.7 = 70%)
    maxSizeKB: 200 // حداکثر سایز برای فایل‌های غیر تصویری
};

// بررسی نوع فایل
function isImageFile(file) {
    return file.type.startsWith('image/');
}

// فشرده‌سازی تصویر
function compressImage(file) {
    return new Promise((resolve, reject) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();
        
        img.onload = function() {
            // محاسبه ابعاد جدید با حفظ نسبت تصویر
            let width = img.width;
            let height = img.height;
            
            if (width > COMPRESSION_SETTINGS.maxWidth) {
                height = (height * COMPRESSION_SETTINGS.maxWidth) / width;
                width = COMPRESSION_SETTINGS.maxWidth;
            }
            
            if (height > COMPRESSION_SETTINGS.maxHeight) {
                width = (width * COMPRESSION_SETTINGS.maxHeight) / height;
                height = COMPRESSION_SETTINGS.maxHeight;
            }
            
            // تنظیم ابعاد کانواس
            canvas.width = width;
            canvas.height = height;
            
            // رسم تصویر فشرده شده
            ctx.drawImage(img, 0, 0, width, height);
            
            // تبدیل به blob
            canvas.toBlob(
                (blob) => {
                    if (blob) {
                        // ایجاد فایل جدید با نام و نوع اصلی
                        const compressedFile = new File(
                            [blob], 
                            file.name, 
                            { 
                                type: file.type,
                                lastModified: new Date().getTime()
                            }
                        );
                        resolve(compressedFile);
                    } else {
                        reject(new Error('فشرده‌سازی تصویر ناموفق بود'));
                    }
                },
                file.type,
                COMPRESSION_SETTINGS.quality
            );
        };
        
        img.onerror = reject;
        img.src = URL.createObjectURL(file);
    });
}

// بررسی سایز فایل
function checkFileSize(file) {
    const maxSizeBytes = COMPRESSION_SETTINGS.maxSizeKB * 1024;
    return file.size <= maxSizeBytes;
}

// فرمت سایز فایل برای نمایش
function formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

// حذف فایل از لیست
function removeFile(index) {
    selectedFiles.value.splice(index, 1);
    uploadFileData.value.selectedList.splice(index, 1);
}

// پردازش فایل انتخابی
async function onFileSelected(e) {
    const files = Array.from(e.target.files);
    
    for (const file of files) {
        try {
            let processedFile = file;
            
            if (isImageFile(file)) {
                // فشرده‌سازی تصاویر
                processedFile = await compressImage(file);
                console.log(`تصویر فشرده شده: ${file.name} -> ${formatFileSize(processedFile.size)}`);
            } else {
                // بررسی سایز فایل‌های غیر تصویری
                if (!checkFileSize(file)) {
                    alert(`فایل ${file.name} بزرگتر از ${COMPRESSION_SETTINGS.maxSizeKB}KB است.`);
                    continue;
                }
            }
            
            // اضافه کردن فایل پردازش شده به لیست
            selectedFiles.value.push(processedFile);
            uploadFileData.value.selectedList.push(processedFile);
            
        } catch (error) {
            console.error('خطا در پردازش فایل:', error);
            alert(`خطا در پردازش فایل ${file.name}: ${error.message}`);
        }
    }
    
    // ریست کردن input فایل برای امکان انتخاب مجدد همان فایل
    e.target.value = '';
}

function confirm() {
    return uploadFileData.value;
}
</script>

<style scoped>
.file-upload {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.upload-row {
    display: flex;
    gap: 12px;
    align-items: center;
    width: 100%;
}

.choose-col {
    flex: 0 0 auto;
}

.desc-col {
    flex: 1 1 auto;
    min-width: 180px;
}

@media (max-width: 768px) {
    .upload-row {
        flex-direction: column;
        align-items: stretch;
    }

    .choose-col,
    .desc-col {
        width: 100%;
    }
}

/* پنهان کردن input فایل */
.hidden-input {
    display: none;
}

/* اینپوت توضیح */
.desc-input {
    border: 1px solid #ccc;
    border-radius: 8px;
    padding: 8px;
    width: 100%;
}

/* لیست فایل‌ها */
.file-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 8px;
}

.file-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    background-color: #f9fafb;
}

.remove-btn {
    background: #ef4444;
    color: white;
    border: none;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
}

.remove-btn:hover {
    background: #dc2626;
}

.btn {
    padding: 8px 16px;
    border: none;
    border-radius: 5px;
    cursor: pointer;
}

.btn-success {
    background: #2563eb;
    color: #fff;
}
</style>